import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { forkJoin, map } from 'rxjs';
import { AppSvgIconComponent, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { GetFormApiResponse } from '@core/api/workspace/models/getFormApiResponse';
import type { ListDataLibraryApiResponse } from '@core/api/workspace/models/listDataLibraryApiResponse';
import type { ListSystemVariablesApiResponse } from '@core/api/workspace/models/listSystemVariablesApiResponse';
import { FormPreviewCardComponent } from '../../components/form-preview-card/form-preview-card.component';
import { FormPreviewHandoffService } from '../../services/form-preview-handoff.service';
import type { BuiltForm } from '../../models/built-form';
import { mapBuiltFormDtoToBuiltForm } from '../../utils/map-built-form-dto';
import {
  enrichBuiltFormLabelTemplates,
  enrichBuiltFormSystemDefaults,
  enrichBuiltFormVariableDefaults,
} from '../../utils/resolve-linked-variable';
import { enrichBuiltFormCollectionRefs } from '../../utils/resolve-collection-dropdown-items';

@Component({
  selector: 'app-form-preview-page',
  standalone: true,
  imports: [AppSvgIconComponent, FormPreviewCardComponent],
  templateUrl: './form-preview-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex min-h-dvh w-full flex-col bg-white',
  },
})
export class FormPreviewPage extends BaseComponent implements OnInit {
  private readonly location = inject(Location);
  private readonly handoff = inject(FormPreviewHandoffService);
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly slug = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('slug')?.trim() ?? '')),
    { initialValue: this.route.snapshot.paramMap.get('slug')?.trim() ?? '' },
  );

  readonly loadState = new State<GetFormApiResponse>();
  readonly form = signal<BuiltForm | null>(null);
  readonly previewMode = signal<'desktop' | 'mobile'>('desktop');
  readonly loadError = signal(false);

  ngOnInit(): void {
    const slug = this.slug();
    if (!slug) {
      this.loadError.set(true);
      return;
    }

    const draft = this.handoff.peekForSlug(slug);
    if (draft) {
      this.previewMode.set(draft.previewMode);
      this.regenerateBoundDefaults(draft.form);
      return;
    }

    this.loadForm(slug);
  }

  setPreviewMode(mode: 'desktop' | 'mobile'): void {
    this.previewMode.set(mode);
  }

  close(): void {
    this.location.back();
  }

  private loadForm(slug: string): void {
    this.loadError.set(false);
    this.executeRequest({
      state: this.loadState,
      request: this.userAPIService.getFormDetails({ slug }),
      handleError: true,
      onSuccess: (response) => {
        const loaded = mapBuiltFormDtoToBuiltForm(response.data.definition);
        this.regenerateBoundDefaults(loaded);
      },
      onFailed: () => {
        this.loadError.set(true);
        this.form.set(null);
      },
    });
  }

  /** Re-resolve data-library and system defaults on each preview open. */
  private regenerateBoundDefaults(form: BuiltForm): void {
    this.executeRequest({
      state: new State<
        [ListSystemVariablesApiResponse, ListDataLibraryApiResponse]
      >(),
      request: forkJoin([
        this.userAPIService.listSystemVariables(),
        this.userAPIService.listDataLibrary({
          pageNumber: 1,
          pageSize: 100,
        }),
      ]),
      handleError: false,
      onSuccess: ([systemResponse, libraryResponse]) => {
        const catalog = systemResponse.data?.items ?? [];
        const items = libraryResponse.data?.items ?? [];
        let next = enrichBuiltFormSystemDefaults(form, catalog);
        next = enrichBuiltFormCollectionRefs(
          enrichBuiltFormVariableDefaults(next, items),
          items,
        );
        next = enrichBuiltFormLabelTemplates(next, catalog, items);
        this.form.set(next);
      },
      onFailed: () => {
        this.form.set(form);
      },
    });
  }
}
